import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { LayoutDashboard, Megaphone, User, Luggage, CloudLightning, FileText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { airportAnnouncementsAPI, weatherAlertsAPI } from '../../services/api';
import AppShell from './AppShell';

const BASE_NAV_ITEMS = [
  { to: '/supervisor/dashboard',       icon: LayoutDashboard, label: 'Live Gates' },
  { to: '/supervisor/announcements',   icon: Megaphone,       label: 'Announcements' },
  { to: '/supervisor/belts',           icon: Luggage,         label: 'Belts' },
  { to: '/supervisor/weather-alerts',  icon: CloudLightning,  label: 'Weather Alerts' },
  { to: '/supervisor/profile',         icon: User,            label: 'Profile' },
];

// CR 11 — requesting a new template is the one function that's Manager-only,
// not shared with Supervisor.
const MANAGER_ONLY_ITEM = { to: '/manager/request-template', icon: FileText, label: 'Request Template' };

export default function SupervisorLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [weatherAlertActive, setWeatherAlertActive] = useState(false);

  const loadPending = useCallback(async () => {
    try {
      const res = await airportAnnouncementsAPI.getPending();
      setPendingCount((res.data.data || []).length);
    } catch { /* non-critical */ }
  }, []);

  const loadWeatherAlertStatus = useCallback(async () => {
    try {
      const res = await weatherAlertsAPI.getActive();
      setWeatherAlertActive((res.data.data || []).length > 0);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    loadPending();
    loadWeatherAlertStatus();
    const interval = setInterval(loadPending, 30000);
    const weatherInterval = setInterval(loadWeatherAlertStatus, 15000);
    return () => { clearInterval(interval); clearInterval(weatherInterval); };
  }, [loadPending, loadWeatherAlertStatus]);

  // Refresh badge when navigating away from announcements page
  useEffect(() => {
    if (!location.pathname.includes('announcements')) loadPending();
  }, [location.pathname, loadPending]);

  const navItems = (user?.role === 'manager' ? [...BASE_NAV_ITEMS, MANAGER_ONLY_ITEM] : BASE_NAV_ITEMS)
    .map(item => {
      if (item.label === 'Announcements' && pendingCount > 0) {
        return { ...item, badge: (
          <span className="bg-orange-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
            {pendingCount}
          </span>
        ) };
      }
      if (item.label === 'Weather Alerts' && weatherAlertActive) {
        return { ...item, badge: (
          <span className="bg-red-600 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center animate-pulse">
            ●
          </span>
        ) };
      }
      return item;
    });

  const mobileHeaderExtra = (
    <>
      {weatherAlertActive && (
        <span className="bg-red-600 text-white text-xs font-bold rounded-full px-2 py-0.5 animate-pulse">
          lightning alert
        </span>
      )}
      {pendingCount > 0 && (
        <span className="bg-orange-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
          {pendingCount} pending
        </span>
      )}
    </>
  );

  return (
    <AppShell
      roleLabel={user?.role === 'manager' ? 'Manager' : 'Supervisor'}
      navItems={navItems}
      mobileHeaderExtra={mobileHeaderExtra}
    />
  );
}
